import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronDown, Plus, X, Phone, Calendar as CalendarIcon, Mail, Bold, Link, List, Image, Clock, Upload, Search } from 'lucide-react';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getAuth } from 'firebase/auth';
// `Link` is already the lucide icon in this file, so the router link is aliased.
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { getTasksByAccount, getTasksByUser, createTask, updateTask, deleteTask, toggleTaskCompletion, uploadTaskFile } from '../services/taskService';
import { getInternalUsers } from '../services/userService';
import {
  getAccounts,
  getCrmContactDisplayPhotoUrl,
  getCrmCompanyLogoDisplayUrl,
  getCrmTeamMemberDisplayPhotoUrl,
} from '../services/firestoreService';
import { fetchPortalUsersLinkedToAccounts } from '../services/accountTeamMembersService';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import { useResponsive } from '../contexts/ResponsiveContext';

// Stable key for a task's company: the account id when we have one, otherwise the
// company name (older tasks were saved with a name but no id).
const taskCompanyKey = (task) => {
  if (task?.companyId) return String(task.companyId);
  const name = String(task?.companyName || '').trim();
  return name ? `name:${name.toLowerCase()}` : '';
};

// Display name + logo for a task's company. Internal tasks carry the BBA emblem;
// legacy tasks saved with a name but no id are matched on the `name:internal` key.
// `path` is the CRM overview route when we can resolve the account (legacy
// name-only tasks and internal tasks have nowhere to go).
const getTaskCompanyDisplay = (task, accounts = []) => {
  const key = taskCompanyKey(task);
  if (!key) return null;
  const account = accounts.find(a => a.id === task?.companyId);
  const isInternal = key === 'internal' || /^name:internal$/.test(key);
  return {
    id: key,
    name: account?.company || task?.companyName || (isInternal ? 'Internal' : 'Unknown'),
    logoUrl: isInternal ? '/bba-emblem.svg' : (account ? getCrmCompanyLogoDisplayUrl(account) : ''),
    isInternal,
    path: (!isInternal && account)
      ? `/crm/${isClientAccount(account) ? 'client' : 'lead'}/${account.id}`
      : null,
  };
};

// Small square company logo with a first-letter fallback, matching the company picker rows.
const CompanyLogoBadge = ({ logoUrl, name }) => (
  <span className="w-6 h-6 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden flex-shrink-0 ring-1 ring-gray-200 dark:ring-gray-600 text-[11px] font-medium text-gray-600 dark:text-gray-300">
    {logoUrl ? (
      <img
        src={logoUrl}
        alt=""
        className="w-full h-full object-contain p-0.5"
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    ) : (
      (name?.[0] || 'C').toUpperCase()
    )}
  </span>
);

// Company name + logo that opens the account's CRM overview. Falls back to plain
// text for internal tasks and legacy tasks that carry a name but no account id.
// stopPropagation keeps a click off the row's "open task" handler.
const TaskCompanyLink = ({ company, className = '' }) => {
  if (!company) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const content = (
    <>
      <CompanyLogoBadge logoUrl={company.logoUrl} name={company.name} />
      <span className="truncate">{company.name}</span>
    </>
  );
  if (!company.path) {
    return <span className={`flex items-center gap-2 min-w-0 ${className}`}>{content}</span>;
  }
  return (
    <RouterLink
      to={company.path}
      onClick={(e) => e.stopPropagation()}
      title={`Open ${company.name}`}
      className={`flex items-center gap-2 min-w-0 -mx-1.5 px-1.5 py-0.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 dark:hover:text-red-400 transition-colors ${className}`}
    >
      {content}
    </RouterLink>
  );
};

// A client account is anything typed/staged as a client in the CRM. `type` has
// drifted across casings/singular-plural over time, so normalize before matching.
const isClientAccount = (account) => {
  const type = String(account?.type || '').trim().toLowerCase();
  if (type === 'client' || type === 'clients') return true;
  return String(account?.stage || '').toLowerCase().includes('client');
};

// Fallback "team" when an account has no linked portal users yet: the CRM primary contact.
const primaryContactAsMember = (account) => {
  const c = account?.contact || {};
  const firstName = c.firstName || '';
  const lastName = c.lastName || '';
  const name = (c.name || `${firstName} ${lastName}`.trim()).trim();
  if (!name) return null;
  return {
    uid: `${account.id}-primary`,
    name,
    firstName: firstName || name.split(' ')[0],
    lastName,
    email: c.email || account?.email || '',
    profileImage: '',
    isPrimary: true,
  };
};

/**
 * The people row for a client in the assignee picker — mirrors the CRM Portal View
 * header: a photo + name pill per team member, red ring on the primary contact.
 * One or two people show full names; three or more collapse to first names.
 */
const ClientTeamChips = ({ account, members }) => {
  if (!members.length) return null;
  const firstNamesOnly = members.length > 2;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {members.map((member) => {
        const photoUrl = member.isPrimary
          ? getCrmContactDisplayPhotoUrl(account) || member.profileImage || ''
          : getCrmTeamMemberDisplayPhotoUrl(account, member.uid) || member.profileImage || '';
        const initials =
          `${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`.trim() ||
          (member.name || member.email || '?').charAt(0);
        const label = firstNamesOnly
          ? member.firstName || String(member.name || '').split(' ')[0] || member.name
          : member.name;

        return (
          <span
            key={member.uid}
            title={member.email || member.name}
            className={`inline-flex items-center gap-1.5 pl-1 pr-2.5 py-0.5 rounded-full text-xs font-medium ${
              member.isPrimary
                ? 'bg-red-50 dark:bg-red-900/20 text-gray-800 dark:text-gray-100'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
            }`}
          >
            <span
              className={`relative h-6 w-6 rounded-full overflow-hidden shrink-0 ${
                member.isPrimary
                  ? 'bg-red-100 dark:bg-red-900 ring-2 ring-red-500'
                  : 'bg-indigo-100 dark:bg-indigo-900'
              }`}
            >
              <span
                className={`w-full h-full flex items-center justify-center text-[10px] font-semibold ${
                  member.isPrimary
                    ? 'text-red-600 dark:text-red-300'
                    : 'text-indigo-600 dark:text-indigo-400'
                }`}
              >
                {String(initials).toUpperCase()}
              </span>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover z-10"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
            </span>
            {label}
          </span>
        );
      })}
    </div>
  );
};

const Tasks = () => {
  const { accountId } = useParams();
  const location = useLocation();
  const auth = getAuth();
  const { isMobile, isTablet } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize with empty tasks array
  const [tasks, setTasks] = useState([]);

  // States
  const [activeTab, setActiveTab] = useState('All');
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);

  // Helper function to safely convert date to timestamp for sorting
  const getDateTimestamp = (value) => {
    if (!value) return Number.MAX_SAFE_INTEGER; // No date goes to the end
    try {
      let date = null;
      if (value && typeof value.toDate === 'function') {
        // Firestore Timestamp
        date = value.toDate();
      } else if (value instanceof Date) {
        // JavaScript Date
        date = value;
      } else if (typeof value === 'number') {
        // Number timestamp
        date = new Date(value);
      } else if (typeof value === 'string') {
        // String date
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) date = parsed;
      } else if (value && typeof value.seconds === 'number') {
        // Plain Timestamp-like object
        date = new Date(value.seconds * 1000);
      }
      
      if (date && !isNaN(date.getTime())) {
        return date.getTime();
      }
    } catch (e) {
      // fall through to return max value
    }
    return Number.MAX_SAFE_INTEGER; // Invalid dates go to the end
  };

  // Add function to filter tasks based on active tab.
  // `ignoreCompany` skips the company filter so the smart company row can count the
  // tasks each company would contribute under the *other* active filters.
  const getFilteredTasks = ({ ignoreCompany = false } = {}) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const endOfWeek = new Date(today);
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));

    // Calculate start and end of current month
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // First apply tab filtering
    let filteredTasks = tasks;
    switch (activeTab) {
      case 'Overdue':
        filteredTasks = tasks.filter(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          return dueDate && dueDate < today;
        });
        break;
      case 'Today':
        filteredTasks = tasks.filter(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          return dueDate && dueDate.toDateString() === today.toDateString();
        });
        break;
      case 'Tomorrow':
        filteredTasks = tasks.filter(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          return dueDate && dueDate.toDateString() === tomorrow.toDateString();
        });
        break;
      case 'This week':
        filteredTasks = tasks.filter(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          return dueDate && dueDate >= today && dueDate <= endOfWeek;
        });
        break;
      case 'This month':
        filteredTasks = tasks.filter(task => {
          const dueDate = task.dueDate ? new Date(task.dueDate) : null;
          return dueDate && dueDate >= startOfMonth && dueDate <= endOfMonth;
        });
        break;
      case 'No date':
        filteredTasks = tasks.filter(task => !task.dueDate);
        break;
      default:
        filteredTasks = tasks;
    }

    // Then apply search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredTasks = filteredTasks.filter(task => 
        (task.name?.toLowerCase() || '').includes(query) ||
        (task.description?.toLowerCase() || '').includes(query)
      );
    }

    // Apply date range filter
    if (filters.dateRange.start || filters.dateRange.end) {
      filteredTasks = filteredTasks.filter(task => {
        if (!task.dueDate) return false;
        const dueDate = new Date(task.dueDate);
        const start = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
        const end = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
        
        if (start && end) {
          return dueDate >= start && dueDate <= end;
        } else if (start) {
          return dueDate >= start;
        } else if (end) {
          return dueDate <= end;
        }
        return true;
      });
    }

    // Apply created by filter
    if (filters.createdBy) {
      filteredTasks = filteredTasks.filter(task => task.createdBy === filters.createdBy);
    }

    // Apply assigned to filter
    if (filters.assignedTo.length > 0) {
      filteredTasks = filteredTasks.filter(task => 
        task.assignees.some(assignee => filters.assignedTo.includes(assignee.id))
      );
    }


    // Apply company filter
    if (filters.companyId && !ignoreCompany) {
      filteredTasks = filteredTasks.filter(task => taskCompanyKey(task) === filters.companyId);
    }

    // Apply status filter
    if (filters.status !== 'all') {
      filteredTasks = filteredTasks.filter(task => 
        filters.status === 'done' ? task.completed : !task.completed
      );
    }

    // Sort by due date
    filteredTasks = [...filteredTasks].sort((a, b) => {
      const dateA = getDateTimestamp(a.dueDate);
      const dateB = getDateTimestamp(b.dueDate);
      return dateA - dateB; // Ascending order: earliest dates first
    });

    return filteredTasks;
  };

  // Get paginated tasks
  const getPaginatedTasks = () => {
    const filtered = getFilteredTasks();
    const startIndex = (currentPage - 1) * tasksPerPage;
    const endIndex = startIndex + tasksPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    const filtered = getFilteredTasks();
    return Math.ceil(filtered.length / tasksPerPage);
  };

  // States for new task form
  const [newTask, setNewTask] = useState({
    title: '',
    dueDate: null,
    description: '',
    assignees: [],
    companyId: null,
    companyName: '',
    completed: false,
    files: []
  });

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyModalTarget, setCompanyModalTarget] = useState('new');
  const [showCompanyInternalTab, setShowCompanyInternalTab] = useState(true);
  const [showCompanyAccountsTab, setShowCompanyAccountsTab] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [newTaskCompanyManuallySet, setNewTaskCompanyManuallySet] = useState(false);
  const [editTaskCompanyManuallySet, setEditTaskCompanyManuallySet] = useState(false);

  const isFathomTask = (task) => task?.source === 'fathom' || !!task?.fathomRecordingId;

  const deriveCompanyFromAssignees = (assignees) => {
    const clientAssignee = assignees.find((a) => a.type === 'client');
    if (clientAssignee) {
      return { companyId: clientAssignee.id, companyName: clientAssignee.name };
    }
    if (assignees.length > 0 && assignees.every((a) => a.type !== 'client')) {
      return { companyId: 'internal', companyName: 'Internal' };
    }
    return { companyId: null, companyName: '' };
  };

  const selectCompany = (company, target) => {
    if (target === 'new') {
      setNewTaskCompanyManuallySet(true);
      setNewTask((prev) => ({ ...prev, companyId: company.id, companyName: company.name }));
    } else {
      setEditTaskCompanyManuallySet(true);
      setEditingTask((prev) => ({ ...prev, companyId: company.id, companyName: company.name }));
    }
    setShowCompanyModal(false);
  };

  // States for users and accounts
  const [internalUsers, setInternalUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [clientUsers, setClientUsers] = useState({}); // Store client user info by user ID

  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);

  // Add new state for edit modal
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Add new state for filters
  const [filters, setFilters] = useState({
    dateRange: {
      start: null,
      end: null
    },
    createdBy: null,
    assignedTo: [],
    companyId: null,
    status: 'not-done', // Changed default to 'not-done'
    searchQuery: ''
  });

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 10;

  // Add useEffect to set default filters when component loads
  useEffect(() => {
    if (auth.currentUser) {
      setFilters(prev => ({
        ...prev,
        assignedTo: [auth.currentUser.uid],
        status: 'not-done'
      }));
    }
  }, [auth.currentUser]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab]);

  const [showAssignedToFilter, setShowAssignedToFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showTimeframeFilter, setShowTimeframeFilter] = useState(false);
  const [showNewTaskInternalTab, setShowNewTaskInternalTab] = useState(true);
  const [showNewTaskAccountsTab, setShowNewTaskAccountsTab] = useState(false);
  const [newTaskAccountSearchTerm, setNewTaskAccountSearchTerm] = useState('');

  // Company filter — options come from the companies actually present on the loaded tasks
  const [showCompanyFilter, setShowCompanyFilter] = useState(false);
  const [companyFilterSearchTerm, setCompanyFilterSearchTerm] = useState('');

  const allCompanyFilterOptions = useMemo(() => {
    const byKey = new Map();
    tasks.forEach((task) => {
      const company = getTaskCompanyDisplay(task, accounts);
      if (!company || byKey.has(company.id)) return;
      byKey.set(company.id, company);
    });
    return [...byKey.values()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [tasks, accounts]);

  const companyFilterOptions = useMemo(() => {
    const term = companyFilterSearchTerm.trim().toLowerCase();
    if (!term) return allCompanyFilterOptions;
    return allCompanyFilterOptions.filter(o => o.name.toLowerCase().includes(term));
  }, [allCompanyFilterOptions, companyFilterSearchTerm]);

  const selectedCompanyFilterLabel = filters.companyId
    ? (allCompanyFilterOptions.find(o => o.id === filters.companyId)?.name || 'Selected')
    : 'All';

  // User-chosen chip order (drag to reorder), saved per user on
  // users/{uid}.taskCompanyChipOrder. Companies not in the saved order fall back
  // to the default sort, after the ordered ones.
  const [companyChipOrder, setCompanyChipOrder] = useState([]);
  const [draggingChipId, setDraggingChipId] = useState(null);
  const companyChipOrderRef = useRef([]);
  companyChipOrderRef.current = companyChipOrder;

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        const saved = snap.exists() ? snap.data().taskCompanyChipOrder : null;
        if (Array.isArray(saved)) setCompanyChipOrder(saved);
      })
      .catch((err) => console.error('Error loading company chip order:', err));
  }, [auth.currentUser]);

  const saveCompanyChipOrder = (order) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setDoc(doc(db, 'users', uid), { taskCompanyChipOrder: order }, { merge: true })
      .catch((err) => console.error('Error saving company chip order:', err));
  };

  // Smart filter row — the companies actually present on the task list the user is
  // looking at right now (every other filter applied), each with its task count so
  // they can jump straight to one company. User's saved order first; otherwise
  // internal first, then busiest company.
  const smartCompanyChips = (() => {
    const scopedTasks = getFilteredTasks({ ignoreCompany: true });
    const byKey = new Map();
    scopedTasks.forEach((task) => {
      const company = getTaskCompanyDisplay(task, accounts);
      if (!company) return;
      const existing = byKey.get(company.id);
      if (existing) existing.count += 1;
      else byKey.set(company.id, { ...company, count: 1 });
    });
    return [...byKey.values()].sort((a, b) => {
      const aIdx = companyChipOrder.indexOf(a.id);
      const bIdx = companyChipOrder.indexOf(b.id);
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      if (a.isInternal !== b.isInternal) return a.isInternal ? -1 : 1;
      if (b.count !== a.count) return b.count - a.count;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  })();

  const smartCompanyTotal = smartCompanyChips.reduce((sum, c) => sum + c.count, 0);

  // Live reorder while dragging: move the dragged chip into the hovered chip's slot
  // once the cursor crosses that chip's midpoint (prevents jitter between chips of
  // different widths). Hidden companies keep their place at the end of the order.
  const handleChipDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggingChipId || draggingChipId === targetId) return;
    const ids = smartCompanyChips.map(c => c.id);
    const from = ids.indexOf(draggingChipId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const mid = rect.left + rect.width / 2;
    if ((from < to && e.clientX < mid) || (from > to && e.clientX > mid)) return;
    ids.splice(from, 1);
    ids.splice(to, 0, draggingChipId);
    setCompanyChipOrder(prev => [...ids, ...prev.filter(id => !ids.includes(id))]);
  };

  const handleChipDragEnd = () => {
    setDraggingChipId(null);
    saveCompanyChipOrder(companyChipOrderRef.current);
  };

  // Portal users linked to each client account, for the Clients tab people row
  const [clientTeamMembers, setClientTeamMembers] = useState({});
  const [clientTeamMembersLoading, setClientTeamMembersLoading] = useState(false);
  const clientTeamMembersRequested = useRef(false);

  const clientAccounts = useMemo(() => accounts.filter(isClientAccount), [accounts]);

  // Load the client rosters the first time a Clients tab is opened
  useEffect(() => {
    const clientsTabOpen =
      (showAssigneesModal && showNewTaskAccountsTab) ||
      (showCompanyModal && showCompanyAccountsTab);
    if (!clientsTabOpen) return;
    if (clientTeamMembersRequested.current || clientAccounts.length === 0) return;
    clientTeamMembersRequested.current = true;

    let cancelled = false;
    (async () => {
      try {
        setClientTeamMembersLoading(true);
        const map = await fetchPortalUsersLinkedToAccounts(clientAccounts);
        if (!cancelled) setClientTeamMembers(Object.fromEntries(map));
      } catch (err) {
        console.error('Could not load client team members:', err);
        clientTeamMembersRequested.current = false;
      } finally {
        if (!cancelled) setClientTeamMembersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showAssigneesModal, showNewTaskAccountsTab, showCompanyModal, showCompanyAccountsTab, clientAccounts]);

  // Roster for a client: linked portal users, or the CRM primary contact as a fallback
  const getClientRoster = (account) => {
    const linked = clientTeamMembers[account.id] || [];
    if (linked.length) return linked;
    const primary = primaryContactAsMember(account);
    return primary ? [primary] : [];
  };

  useEffect(() => {
    if (!newTaskCompanyManuallySet && newTask.assignees.length > 0) {
      const { companyId, companyName } = deriveCompanyFromAssignees(newTask.assignees);
      setNewTask((prev) => ({ ...prev, companyId, companyName }));
    }
  }, [newTask.assignees, newTaskCompanyManuallySet]);

  useEffect(() => {
    if (!editTaskCompanyManuallySet && editingTask?.assignees?.length > 0) {
      const { companyId, companyName } = deriveCompanyFromAssignees(editingTask.assignees);
      setEditingTask((prev) => (prev ? { ...prev, companyId, companyName } : prev));
    }
  }, [editingTask?.assignees, editTaskCompanyManuallySet]);

  // Safely format due dates coming from various sources (Firestore Timestamp, Date, string, number)
  const formatDueDate = (value) => {
    if (!value) return 'No date';
    let date = null;
    try {
      if (value && typeof value.toDate === 'function') {
        date = value.toDate();
      } else if (value instanceof Date) {
        date = value;
      } else if (typeof value === 'number') {
        date = new Date(value);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) date = parsed;
      } else if (value && typeof value.seconds === 'number') {
        // Support plain Timestamp-like object
        date = new Date(value.seconds * 1000);
      }
    } catch (e) {
      // fall through to return 'No date'
    }
    return date && !isNaN(date.getTime()) ? date.toLocaleDateString() : 'No date';
  };

  // Check if a task is overdue
  const isTaskOverdue = (task) => {
    if (task.completed || !task.dueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let dueDate = null;
    try {
      if (task.dueDate && typeof task.dueDate.toDate === 'function') {
        dueDate = task.dueDate.toDate();
      } else if (task.dueDate instanceof Date) {
        dueDate = task.dueDate;
      } else if (typeof task.dueDate === 'number') {
        dueDate = new Date(task.dueDate);
      } else if (typeof task.dueDate === 'string') {
        const parsed = new Date(task.dueDate);
        if (!isNaN(parsed.getTime())) dueDate = parsed;
      } else if (task.dueDate && typeof task.dueDate.seconds === 'number') {
        dueDate = new Date(task.dueDate.seconds * 1000);
      }
    } catch (e) {
      return false;
    }
    
    if (!dueDate || isNaN(dueDate.getTime())) return false;
    
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Add refs for all dropdowns
  const assignedToDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const timeframeDropdownRef = useRef(null);
  const companyFilterDropdownRef = useRef(null);

  // Add new state for view modal
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);

  // Load tasks, users and accounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load internal users first
        const users = await getInternalUsers();
        console.log('Internal users loaded in Tasks:', users);
        setInternalUsers(users);

        // Load accounts
        const loadedAccounts = await getAccounts();
        console.log('Loaded accounts:', loadedAccounts);
        console.log('Account types found:', [...new Set(loadedAccounts.map(a => a.type))]);
        console.log('Account stages found:', [...new Set(loadedAccounts.map(a => a.stage))]);
        console.log('Client accounts (type=clients):', loadedAccounts.filter(account => account.type === 'clients'));
        console.log('Client accounts (type=Clients):', loadedAccounts.filter(account => account.type === 'Clients'));
        console.log('Client accounts (stage contains client):', loadedAccounts.filter(account => account.stage && account.stage.toLowerCase().includes('client')));
        setAccounts(loadedAccounts);

        // Load tasks based on current filter or accountId
        let loadedTasks;
        if (accountId) {
          loadedTasks = await getTasksByAccount(accountId);
        } else {
          // Load tasks for the current user when no specific account is selected
          loadedTasks = await getTasksByUser(auth.currentUser.uid);
        }
        
        // Load client user information for task creators
        const clientUserInfo = {};
        for (const task of loadedTasks) {
          if (task.createdBy && task.createdBy !== 'Internal') {
            // Check if it's not an internal user
            const isInternalUser = users.find(u => u.id === task.createdBy);
            if (!isInternalUser) {
              // This might be a client user, try to get their info
              try {
                const userDoc = await getDoc(doc(db, 'users', task.createdBy));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  if (userData.accountId) {
                    const account = loadedAccounts.find(a => a.id === userData.accountId);
                    if (account) {
                      clientUserInfo[task.createdBy] = {
                        id: task.createdBy,
                        name: account.company,
                        initials: account.company?.[0] || 'C',
                        type: 'client',
                        photoURL: account.contact?.photoURL || null
                      };
                    }
                  }
                }
              } catch (error) {
                console.error('Error loading client user info:', error);
              }
            }
          }
        }
        setClientUsers(clientUserInfo);

        // Process assignees to include full user details
        const processedTasks = loadedTasks.map(task => {
          const assigneesWithDetails = (task.assignees || []).map(assigneeId => {
            // First check internal users
            const internalUser = users.find(u => u.id === assigneeId);
            if (internalUser) return internalUser;

            // Then check accounts
            const account = loadedAccounts.find(a => a.id === assigneeId);
            if (account) {
              return {
                id: account.id,
                name: account.company,
                type: 'client',
                initials: account.company?.[0] || 'C',
                photoURL: account.contact?.photoURL || null
              };
            }

            return null;
          }).filter(Boolean);

          return {
            ...task,
            assignees: assigneesWithDetails
          };
        });
        
        setTasks(processedTasks);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (auth.currentUser) {
      loadData();
    }
  }, [accountId, auth.currentUser]);


  // Update the users object to use real data
  const users = {
    internal: internalUsers,
    clients: accounts.map(account => ({
      id: account.id,
      name: account.company,
      type: 'client',
      initials: account.company?.[0] || 'C'
    }))
  };
  
  console.log('Current users object in Tasks:', users);

  // Helper function to get users associated with an account
  const getUsersForAccount = async (accountId) => {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('accountId', '==', accountId));
      const querySnapshot = await getDocs(q);
      
      const userIds = [];
      querySnapshot.forEach((doc) => {
        userIds.push(doc.id);
      });
      
      console.log(`Found ${userIds.length} users for account ${accountId}:`, userIds);
      return userIds;
    } catch (error) {
      console.error('Error getting users for account:', error);
      return [];
    }
  };

  // Refetch tasks when the Assigned To filter targets users other than the current user (outside account context)
  useEffect(() => {
    const refetchForAssignedFilter = async () => {
      try {
        // Only apply this logic when not in an account context
        if (accountId) return;
        if (!auth.currentUser) return;

        // If no assignees selected, default to current user's tasks
        const targetUserIds = (filters.assignedTo && filters.assignedTo.length > 0)
          ? filters.assignedTo
          : [auth.currentUser.uid];

        // Fetch tasks for all selected users
        const results = await Promise.all(targetUserIds.map(id => getTasksByUser(id)));
        const combined = results.flat();

        // De-duplicate by task id
        const uniqueById = Array.from(new Map(combined.map(t => [t.id, t])).values());

        // Process assignees to include full user/account details for UI
        const processedTasks = uniqueById.map(task => {
          const assigneesWithDetails = (task.assignees || []).map(assigneeId => {
            const internalUser = internalUsers.find(u => u.id === assigneeId);
            if (internalUser) return internalUser;
            const account = accounts.find(a => a.id === assigneeId);
            if (account) {
              return {
                id: account.id,
                name: account.company,
                type: 'client',
                initials: account.company?.[0] || 'C',
                photoURL: account.contact?.photoURL || null
              };
            }
            return null;
          }).filter(Boolean);

          return {
            ...task,
            assignees: assigneesWithDetails
          };
        });

        setTasks(processedTasks);
      } catch (err) {
        console.error('Error refetching tasks for assigned filter:', err);
      }
    };

    refetchForAssignedFilter();
  }, [filters.assignedTo, accountId, auth.currentUser, internalUsers, accounts]);

  // Add click outside handler for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle clicks that are not on dropdown buttons or their children
      const isDropdownButton = event.target.closest('button');
      if (isDropdownButton) return;

      // Filter dropdowns
      if (assignedToDropdownRef.current && !assignedToDropdownRef.current.contains(event.target)) {
        setShowAssignedToFilter(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
      if (timeframeDropdownRef.current && !timeframeDropdownRef.current.contains(event.target)) {
        setShowTimeframeFilter(false);
      }
      if (companyFilterDropdownRef.current && !companyFilterDropdownRef.current.contains(event.target)) {
        setShowCompanyFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingTask, newTask]);

  const handleCreateTask = async () => {
    try {
      // Validate required fields
      if (!newTask.title.trim()) {
        setError('Task title is required');
        return;
      }

      if (newTask.assignees.length === 0) {
        setError('At least one assignee is required');
        return;
      }

      // Determine accountId from company or assignees
      let taskAccountId = null;
      if (newTask.companyId && newTask.companyId !== 'internal') {
        taskAccountId = newTask.companyId;
      } else {
        const accountAssignee = newTask.assignees.find(assignee => assignee.type === 'client');
        if (accountAssignee) {
          taskAccountId = accountAssignee.id;
        } else if (accountId) {
          taskAccountId = accountId;
        }
      }

      // For client accounts, also get the user IDs associated with the account
      const allAssigneeIds = [...newTask.assignees.map(a => a.id)];
      for (const assignee of newTask.assignees) {
        // Check if this is a client account (not an internal user)
        if (assignee.type === 'client') {
          // This is a client account, get associated users
          const accountUsers = await getUsersForAccount(assignee.id);
          allAssigneeIds.push(...accountUsers);
        }
      }
      
      // Remove duplicates
      const uniqueAssigneeIds = [...new Set(allAssigneeIds)];
      console.log('Final assignees (including account users):', uniqueAssigneeIds);

      // Build task object
      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        dueDate: newTask.dueDate,
        assignees: uniqueAssigneeIds,
        accountId: taskAccountId,
        companyId: newTask.companyId || null,
        companyName: newTask.companyName || null,
        completed: false,
        createdBy: auth.currentUser.uid,
        files: [], // Will be populated after task creation
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Create task in Firestore
      const createdTask = await createTask(taskData);
      
      // Upload files if any
      if (newTask.files && newTask.files.length > 0) {
        try {
          const uploadPromises = newTask.files.map(file => uploadTaskFile(file, createdTask.id));
          const uploadedFiles = await Promise.all(uploadPromises);
          
          // Update the task with uploaded file information
          await updateTask(createdTask.id, { files: uploadedFiles });
          createdTask.files = uploadedFiles;
        } catch (fileError) {
          console.error('Error uploading files:', fileError);
          setError(`Task created but failed to upload files: ${fileError.message}`);
        }
      }
      
      // Only add to local state if current user is assigned to the task
      const currentUserId = auth.currentUser.uid;
      const isCurrentUserAssigned = newTask.assignees.some(assignee => assignee.id === currentUserId);
      
      if (isCurrentUserAssigned) {
        // Update local state with full task data including assignee details
        const taskWithDetails = {
          ...createdTask,
          assignees: newTask.assignees // Keep the full assignee objects for display
        };
        
        setTasks(prevTasks => [...prevTasks, taskWithDetails]);
      }
      
      // Reset form
      setNewTask({
        title: '',
        dueDate: null,
        description: '',
        assignees: [],
        companyId: null,
        companyName: '',
        completed: false,
        files: []
      });
      setNewTaskCompanyManuallySet(false);
      
      // Close modal
      setShowNewTaskModal(false);
      
      // Clear any previous errors
      setError(null);
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err.message || 'Failed to create task. Please try again.');
    }
  };

  const toggleAssignee = (user) => {
    const isSelected = newTask.assignees.find(a => a.id === user.id);
    if (isSelected) {
      setNewTask({
        ...newTask,
        assignees: newTask.assignees.filter(a => a.id !== user.id)
      });
    } else {
      setNewTask({
        ...newTask,
        assignees: [...newTask.assignees, user]
      });
    }
  };

  const handleTaskCompletion = async (taskId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const updatedTask = await toggleTaskCompletion(taskId, !task.completed);
      setTasks(prevTasks => 
        prevTasks.map(t => t.id === updatedTask.id ? updatedTask : t)
      );
    } catch (err) {
      console.error('Error toggling task completion:', err);
      setError(err.message || 'Failed to update task status. Please try again.');
    }
  };

  const handleTaskClick = async (task) => {
    try {
      // Get full assignee details for the task
      const assigneesWithDetails = task.assignees.map(assigneeId => {
        // First check internal users
        const internalUser = internalUsers.find(u => u.id === assigneeId);
        if (internalUser) return internalUser;

        // Then check accounts
        const account = accounts.find(a => a.id === assigneeId);
        if (account) {
          return {
            id: account.id,
            name: account.company,
            type: 'client',
            initials: account.company?.[0] || 'C',
            photoURL: account.contact?.photoURL || null
          };
        }

        return null;
      }).filter(Boolean);

      // If task already has full assignee objects, use those instead
      const taskAssignees = Array.isArray(task.assignees) && task.assignees[0]?.name 
        ? task.assignees 
        : assigneesWithDetails;

      setViewingTask({
        ...task,
        assignees: taskAssignees,
        showTimePopup: false,
        files: task.files || [],
        estimatedHours: task.estimatedHours || 0,
        estimatedMinutes: task.estimatedMinutes || 0
      });
      setShowViewTaskModal(true);
    } catch (err) {
      console.error('Error loading task details:', err);
      setError('Failed to load task details. Please try again.');
    }
  };

  const handleSwitchToEdit = () => {
    try {
      // Safely handle the due date - convert Firestore Timestamp or other formats to Date object
      let safeDueDate = null;
      if (viewingTask.dueDate) {
        try {
          if (viewingTask.dueDate.toDate && typeof viewingTask.dueDate.toDate === 'function') {
            // Firestore Timestamp
            safeDueDate = viewingTask.dueDate.toDate();
          } else if (viewingTask.dueDate.getTime && typeof viewingTask.dueDate.getTime === 'function') {
            // JavaScript Date
            safeDueDate = viewingTask.dueDate;
          } else {
            // String or number timestamp
            const date = new Date(viewingTask.dueDate);
            if (!isNaN(date.getTime())) {
              safeDueDate = date;
            } else {
              console.warn('Invalid due date found:', viewingTask.dueDate);
            }
          }
        } catch (error) {
          console.warn('Error parsing due date:', error, viewingTask.dueDate);
        }
      }
      
      // viewingTask.assignees already contains full assignee objects, so we can use them directly
      const editingTaskData = {
        ...viewingTask,
        title: viewingTask.title, // Ensure title is properly set
        dueDate: safeDueDate, // Use the safely parsed date
        assignees: viewingTask.assignees || [], // Use existing assignee objects
        companyId: viewingTask.companyId ?? null,
        companyName: viewingTask.companyName ?? '',
        files: viewingTask.files || [],
        createdBy: viewingTask.createdBy, // Ensure createdBy is preserved
        completed: viewingTask.completed, // Ensure completed status is preserved
        description: viewingTask.description // Ensure description is preserved
      };
      
      setEditingTask(editingTaskData);
      setEditTaskCompanyManuallySet(!!viewingTask.companyId);
      setShowViewTaskModal(false);
      setShowEditTaskModal(true);
    } catch (error) {
      console.error('Error in handleSwitchToEdit:', error);
      setError('Failed to open edit modal. Please try again.');
    }
  };

  const handleEditTask = (task) => {
    // Get full assignee details for the task
    const assigneesWithDetails = task.assignees.map(assigneeId => {
      // First check internal users
      const internalUser = internalUsers.find(u => u.id === assigneeId);
      if (internalUser) return internalUser;

      // Then check accounts
      const account = accounts.find(a => a.id === assigneeId);
      if (account) {
        return {
          id: account.id,
          name: account.company,
          type: 'client',
          initials: account.company?.[0] || 'C'
        };
      }

      return null;
    }).filter(Boolean);

    // Safely handle the due date - convert Firestore Timestamp or other formats to Date object
    let safeDueDate = null;
    if (task.dueDate) {
      try {
        if (task.dueDate.toDate && typeof task.dueDate.toDate === 'function') {
          // Firestore Timestamp
          safeDueDate = task.dueDate.toDate();
        } else if (task.dueDate.getTime && typeof task.dueDate.getTime === 'function') {
          // JavaScript Date
          safeDueDate = task.dueDate;
        } else {
          // String or number timestamp
          const date = new Date(task.dueDate);
          if (!isNaN(date.getTime())) {
            safeDueDate = date;
          } else {
            console.warn('Invalid due date found:', task.dueDate);
          }
        }
      } catch (error) {
        console.warn('Error parsing due date:', error, task.dueDate);
      }
    }

    setEditingTask({
      ...task,
      title: task.title, // Ensure title is properly set
      dueDate: safeDueDate, // Use the safely parsed date
      assignees: assigneesWithDetails,
      companyId: task.companyId ?? null,
      companyName: task.companyName ?? '',
      files: task.files || [],
      createdBy: task.createdBy, // Ensure createdBy is preserved
      completed: task.completed, // Ensure completed status is preserved
      description: task.description // Ensure description is preserved
    });
    setEditTaskCompanyManuallySet(!!task.companyId);
    setShowEditTaskModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Validate required fields
      if (!editingTask.title.trim()) {
        setError('Task title is required');
        return;
      }

      if (editingTask.assignees.length === 0) {
        setError('At least one assignee is required');
        return;
      }

      // Build task update object with only changed fields
      const taskData = {};
      
      // Only include fields that have changed
      if (editingTask.title !== tasks.find(t => t.id === editingTask.id)?.title) {
        taskData.title = editingTask.title.trim();
      }
      if (editingTask.description !== tasks.find(t => t.id === editingTask.id)?.description) {
        taskData.description = editingTask.description.trim();
      }
      // Handle due date comparison - convert both to comparable values
      const currentTask = tasks.find(t => t.id === editingTask.id);
      const currentDueDate = currentTask?.dueDate;
      const newDueDate = editingTask.dueDate;
      
      // Convert both dates to comparable values
      let currentDueDateValue = null;
      let newDueDateValue = null;
      
      if (currentDueDate) {
        if (currentDueDate.toDate && typeof currentDueDate.toDate === 'function') {
          // Firestore Timestamp
          currentDueDateValue = currentDueDate.toDate().getTime();
        } else if (currentDueDate.getTime && typeof currentDueDate.getTime === 'function') {
          // JavaScript Date
          currentDueDateValue = currentDueDate.getTime();
        } else if (typeof currentDueDate === 'string' || typeof currentDueDate === 'number') {
          // String or number timestamp
          currentDueDateValue = new Date(currentDueDate).getTime();
        }
      }
      
      if (newDueDate) {
        if (newDueDate.toDate && typeof newDueDate.toDate === 'function') {
          // Firestore Timestamp
          newDueDateValue = newDueDate.toDate().getTime();
        } else if (newDueDate.getTime && typeof newDueDate.getTime === 'function') {
          // JavaScript Date
          newDueDateValue = newDueDate.getTime();
        } else if (typeof newDueDate === 'string' || typeof newDueDate === 'number') {
          // String or number timestamp
          newDueDateValue = new Date(newDueDate).getTime();
        }
      }
      
      if (currentDueDateValue !== newDueDateValue) {
        taskData.dueDate = editingTask.dueDate;
      }
      if (JSON.stringify(editingTask.assignees.map(a => a.id).sort()) !== 
          JSON.stringify(tasks.find(t => t.id === editingTask.id)?.assignees.map(a => a.id).sort())) {
        taskData.assignees = editingTask.assignees.map(a => a.id);
      }
      if (JSON.stringify(editingTask.files) !== JSON.stringify(tasks.find(t => t.id === editingTask.id)?.files)) {
        taskData.files = editingTask.files;
      }
      if (editingTask.completed !== tasks.find(t => t.id === editingTask.id)?.completed) {
        taskData.completed = editingTask.completed;
      }
      const currentTaskForCompany = tasks.find(t => t.id === editingTask.id);
      if (editingTask.companyId !== currentTaskForCompany?.companyId) {
        taskData.companyId = editingTask.companyId || null;
      }
      if (editingTask.companyName !== currentTaskForCompany?.companyName) {
        taskData.companyName = editingTask.companyName || null;
      }
      if (editingTask.companyId && editingTask.companyId !== 'internal' && editingTask.companyId !== currentTaskForCompany?.accountId) {
        taskData.accountId = editingTask.companyId;
      } else if (editingTask.companyId === 'internal' && currentTaskForCompany?.accountId) {
        taskData.accountId = null;
      }

      // Only update if there are changes
      if (Object.keys(taskData).length > 0) {
        const updatedTask = await updateTask(editingTask.id, taskData);
        
        // Update local state with full task data including assignee details
        const taskWithDetails = {
          ...updatedTask,
          title: editingTask.title, // Ensure title is preserved
          description: editingTask.description, // Ensure description is preserved
          dueDate: editingTask.dueDate, // Ensure due date is preserved
          assignees: editingTask.assignees, // Keep the full assignee objects for display
          files: editingTask.files || [], // Ensure files are preserved
          createdBy: editingTask.createdBy, // Ensure createdBy is preserved
          completed: editingTask.completed // Ensure completed status is preserved
        };
        
        setTasks(prevTasks => 
          prevTasks.map(task => task.id === taskWithDetails.id ? taskWithDetails : task)
        );
      }
      
      setShowEditTaskModal(false);
      setEditingTask(null);
      setError(null);
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.message || 'Failed to update task. Please try again.');
    }
  };

  const handleDeleteTask = async () => {
    try {
      await deleteTask(editingTask.id);
      setTasks(prevTasks => prevTasks.filter(task => task.id !== editingTask.id));
      setShowEditTaskModal(false);
      setEditingTask(null);
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task. Please try again.');
    }
  };


  // Add a separate toggle function for editing task assignees
  const toggleEditAssignee = (user) => {
    const isSelected = editingTask.assignees.find(a => a.id === user.id);
    if (isSelected) {
      setEditingTask({
        ...editingTask,
        assignees: editingTask.assignees.filter(a => a.id !== user.id)
      });
    } else {
      setEditingTask({
        ...editingTask,
        assignees: [...editingTask.assignees, user]
      });
    }
  };

  // Update the handleEditAccountSelect function
  const handleEditAccountSelect = (account) => {
    setEditingTask(prev => ({ 
      ...prev, 
      showAccountDropdown: false,
      accountId: account
    }));
  };

  // Add function to clear all filters
  const clearAllFilters = () => {
    setFilters({
      dateRange: {
        start: null,
        end: null
      },
      assignedTo: [],
      companyId: null,
      status: 'all',
      searchQuery: ''
    });
    setActiveTab('All');
  };

  return (
    <div className={`min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-6'}`}>
      {/* Same ambient BBA background as the Dashboard and hiring board */}
      <PortalBackground />
      <div className="relative">
        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <BBALoader size={48} />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center w-full justify-center">
                <h1 className={`${isMobile ? 'text-2xl' : isTablet ? 'text-3xl' : 'text-4xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Tasks</h1>
              </div>
              <div className="flex items-center gap-4">
                <AddButton onClick={() => setShowNewTaskModal(true)}>
                  <Plus size={18} />
                </AddButton>
              </div>
            </div>


            {/* Search and filters — relative z-30 keeps the open dropdowns above the
                task rows (backdrop-blur makes this bar its own stacking context, so
                without it the rows' z-10 avatars paint on top of the menus) */}
            <div className={`relative z-30 flex ${isMobile ? 'flex-col' : 'items-center'} gap-3 mb-5`}>
              <div className={`relative ${isMobile ? 'w-full' : 'flex-1 min-w-0'}`}>
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search tasks by title, company, or assignee…"
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-10 pr-10 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                {filters.searchQuery && (
                  <button
                    type="button"
                    onClick={() => setFilters(prev => ({ ...prev, searchQuery: '' }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Assigned To Filter */}
                <div className="relative" ref={assignedToDropdownRef}>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAssignedToFilter(!showAssignedToFilter);
                    }}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {filters.assignedTo.length > 0 
                      ? `${filters.assignedTo.length} selected`
                      : 'Assigned to'}
                    {filters.assignedTo.length > 0 && (
                      <X 
                        size={14} 
                        className="ml-1 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters(prev => ({ ...prev, assignedTo: [] }));
                        }}
                      />
                    )}
                    <ChevronDown size={16} />
                  </button>
                  {showAssignedToFilter && (
                    <div className="absolute mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-20 right-0 overflow-hidden w-64">
                      <div className="p-2">
                        {users.internal.map(user => (
                          <div
                            key={user.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newAssignees = filters.assignedTo.includes(user.id)
                                ? filters.assignedTo.filter(id => id !== user.id)
                                : [...filters.assignedTo, user.id];
                              setFilters(prev => ({ ...prev, assignedTo: newAssignees }));
                            }}
                            className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={filters.assignedTo.includes(user.id)}
                              onChange={() => {}}
                              className="mr-2"
                            />
                            <span className="text-gray-900 dark:text-white">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>


                {/* Status Filter */}
                <div className="relative" ref={statusDropdownRef}>
                  <button 
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowStatusFilter(!showStatusFilter)}
                  >
                    Status: {filters.status === 'all' ? 'All' : filters.status === 'done' ? 'Done' : 'Not Done'}
                    {filters.status !== 'all' && (
                      <X 
                        size={14} 
                        className="ml-1 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters(prev => ({ ...prev, status: 'all' }));
                        }}
                      />
                    )}
                    <ChevronDown size={16} />
                  </button>
                  {showStatusFilter && (
                    <div className="absolute mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-20 right-0 overflow-hidden w-48">
                      <div className="p-2">
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setFilters({...filters, status: 'all'});
                            setShowStatusFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">All</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setFilters({...filters, status: 'done'});
                            setShowStatusFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">Done</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setFilters({...filters, status: 'not-done'});
                            setShowStatusFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">Not Done</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Timeframe Filter */}
                <div className="relative" ref={timeframeDropdownRef}>
                  <button 
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowTimeframeFilter(!showTimeframeFilter)}
                  >
                    Timeframe: {activeTab}
                    {activeTab !== 'All' && (
                      <X 
                        size={14} 
                        className="ml-1 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('All');
                        }}
                      />
                    )}
                    <ChevronDown size={16} />
                  </button>
                  {showTimeframeFilter && (
                    <div className="absolute mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-20 right-0 overflow-hidden w-48">
                      <div className="p-2">
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('All');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">All</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('Overdue');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">Overdue</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('Today');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">Today</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('Tomorrow');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">Tomorrow</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('This week');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">This week</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('This month');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">This month</span>
                        </div>
                        <div 
                          className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          onClick={() => {
                            setActiveTab('No date');
                            setShowTimeframeFilter(false);
                          }}
                        >
                          <span className="text-gray-900 dark:text-white">No date</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Filter */}
                <div className="relative" ref={companyFilterDropdownRef}>
                  <button
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm font-medium shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => setShowCompanyFilter(!showCompanyFilter)}
                  >
                    Company: {selectedCompanyFilterLabel}
                    {filters.companyId && (
                      <X
                        size={14}
                        className="ml-1 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilters(prev => ({ ...prev, companyId: null }));
                        }}
                      />
                    )}
                    <ChevronDown size={16} />
                  </button>
                  {showCompanyFilter && (
                    <div className="absolute mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl z-20 right-0 overflow-hidden w-64">
                      <div className="p-2">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companyFilterSearchTerm}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setCompanyFilterSearchTerm(e.target.value)}
                          className="w-full px-2 py-1.5 mb-2 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                        <div className="max-h-60 overflow-y-auto">
                          <div
                            className="p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer rounded-md"
                            onClick={() => {
                              setFilters(prev => ({ ...prev, companyId: null }));
                              setShowCompanyFilter(false);
                            }}
                          >
                            <span className="text-gray-900 dark:text-white">All</span>
                          </div>
                          {companyFilterOptions.map((option) => (
                            <div
                              key={option.id}
                              className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer rounded-md"
                              onClick={() => {
                                setFilters(prev => ({ ...prev, companyId: option.id }));
                                setShowCompanyFilter(false);
                              }}
                            >
                              <CompanyLogoBadge logoUrl={option.logoUrl} name={option.name} />
                              <span className="text-gray-900 dark:text-white truncate">{option.name}</span>
                            </div>
                          ))}
                          {companyFilterOptions.length === 0 && (
                            <div className="p-2 text-sm text-gray-500 dark:text-gray-400">No companies found.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Clear All Filters Button */}
                {(filters.searchQuery ||
                  filters.assignedTo.length > 0 ||
                  filters.status !== 'all' ||
                  filters.companyId ||
                  activeTab !== 'All') && (
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-red-200 dark:border-red-800/60 bg-white dark:bg-gray-800 text-sm font-medium text-red-600 dark:text-red-400 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <X size={14} />
                    Clear all
                  </button>
                )}
              </div>
            </div>

            {/* Smart company filter row — one chip per company on the current task
                list, so the user can jump to a company without opening a dropdown */}
            {smartCompanyChips.length > 0 && (
              <div className="relative z-10 mb-5">
                <div className="chip-scrollbar flex items-center gap-2 overflow-x-auto pb-1.5">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, companyId: null }))}
                    className={`flex items-center gap-1.5 flex-shrink-0 pl-3 pr-2.5 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                      !filters.companyId
                        ? 'border-red-600 bg-red-600 text-white'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    All
                    <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-medium ${!filters.companyId ? 'bg-white/20 text-red-50' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                      {smartCompanyTotal}
                    </span>
                  </button>
                  {smartCompanyChips.map((company) => {
                    const isActive = filters.companyId === company.id;
                    const isDragging = draggingChipId === company.id;
                    return (
                      <button
                        key={company.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', company.id);
                          setDraggingChipId(company.id);
                        }}
                        onDragOver={(e) => handleChipDragOver(e, company.id)}
                        onDrop={(e) => e.preventDefault()}
                        onDragEnd={handleChipDragEnd}
                        onClick={() => setFilters(prev => ({
                          ...prev,
                          companyId: prev.companyId === company.id ? null : company.id,
                        }))}
                        title={`${company.name} — drag to reorder`}
                        className={`flex items-center gap-1.5 flex-shrink-0 pl-1.5 pr-2.5 py-1.5 rounded-full border text-sm font-medium transition-colors cursor-grab active:cursor-grabbing select-none ${
                          isDragging ? 'opacity-40 ring-2 ring-red-300 dark:ring-red-700' : ''
                        } ${
                          isActive
                            ? 'border-red-600 bg-red-600 text-white'
                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                      >
                        <CompanyLogoBadge logoUrl={company.logoUrl} name={company.name} />
                        <span className="max-w-[10rem] truncate">{company.name}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-medium ${isActive ? 'bg-white/20 text-red-50' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                          {company.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Table View */}
            <div className="relative z-0 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
              {/* Table header */}
              <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : isTablet ? 'grid-cols-5' : 'grid-cols-[repeat(14,minmax(0,1fr))]'} border-b border-gray-200 dark:border-gray-800 ${isMobile ? 'py-3 px-3' : 'py-3 px-6'} bg-gray-50 dark:bg-gray-800/60 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400`}>
                <div className={`${isMobile ? '' : isTablet ? 'col-span-2' : 'col-span-4'} text-center`}>
                  Task
                </div>
                <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} text-center`}>
                  Due date
                </div>
                <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} text-center`}>
                  Assigned to
                </div>
                <div className={`${isMobile ? 'hidden' : 'col-span-2'} text-center`}>
                  Company
                </div>
                <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} text-center`}>
                  Created by
                </div>
                <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} text-center`}>
                  Created at
                </div>
              </div>

              {/* Table body */}
              {getPaginatedTasks().length === 0 ? (
                <div className="col-span-10 py-12 text-center text-gray-500 dark:text-gray-400">
                  No tasks found. {getFilteredTasks().length === 0 ? 'Try adjusting your filters.' : 'Try a different page.'}
                </div>
              ) : (
                getPaginatedTasks().map((task, index) => {
                  const overdue = isTaskOverdue(task);
                  return (
                    <div 
                      key={task.id || `task-${index}`} 
                      className={`grid ${isMobile ? 'grid-cols-1 gap-2' : isTablet ? 'grid-cols-5' : 'grid-cols-[repeat(14,minmax(0,1fr))]'} border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${isMobile ? 'py-3 px-3' : 'py-3.5 px-6'} cursor-pointer transition-colors ${
                        task.completed
                          ? 'bg-gray-50/60 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800/70'
                          : overdue
                            ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 border-l-4 border-l-red-500'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                      }`}
                      onClick={() => handleTaskClick(task)}
                    >
                      <div className={`${isMobile ? '' : isTablet ? 'col-span-2' : 'col-span-4'} flex items-center`}>
                        <button
                          onClick={(e) => handleTaskCompletion(task.id, e)}
                          className={`w-5 h-5 shrink-0 rounded-full border ${task.completed ? 'bg-red-500 border-red-500' : 'bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500'} flex items-center justify-center mr-3 hover:border-red-500 transition-colors shadow-sm`}
                        >
                          {task.completed && <Check size={14} className="text-white" />}
                        </button>
                        <div className="min-w-0">
                          <div className={`font-medium break-words [overflow-wrap:anywhere] ${task.completed ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-800 dark:text-white'}`}>{task.title}</div>
                        </div>
                      </div>
                      <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} flex items-center justify-center text-sm text-gray-600 dark:text-gray-400`}>
                        {formatDueDate(task.dueDate)}
                      </div>
                      <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} flex items-center justify-center gap-1 text-sm text-gray-600 dark:text-gray-400`}>
                        {task.assignees.map((assignee, assigneeIndex) => (
                          <div key={assignee.id || `assignee-${assigneeIndex}`} className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs shadow-sm overflow-hidden flex-shrink-0 relative">
                            {(assignee.profileImage || assignee.photoURL) ? (
                              <img
                                src={assignee.profileImage || assignee.photoURL}
                                alt={assignee.name || 'User'}
                                className="w-full h-full object-cover absolute inset-0 z-10"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : null}
                            <span className="w-full h-full flex items-center justify-center">
                              {assignee.initials}
                            </span>
                          </div>
                        ))}
                        {task.assignees.length === 1 && (
                          <span className="break-words [overflow-wrap:anywhere]">{task.assignees[0].name || 'Unknown User'}</span>
                        )}
                      </div>
                      <div className={`${isMobile ? 'hidden' : 'col-span-2'} flex items-center justify-center gap-2 min-w-0 text-sm text-gray-600 dark:text-gray-400`}>
                        <TaskCompanyLink company={getTaskCompanyDisplay(task, accounts)} />
                      </div>
                      <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} flex items-center justify-center text-sm text-gray-600 dark:text-gray-400`}>
                        {isFathomTask(task) ? (
                          <>
                            <img src="/fathomlogo.png" alt="Fathom" className="w-6 h-6 rounded-full mr-2 object-contain" />
                            <span>Fathom</span>
                          </>
                        ) : (() => {
                          const createdByUser = task.createdBy === 'Internal' ? null : (users.internal.find(u => u.id === task.createdBy) || clientUsers[task.createdBy]);
                          const profileImage = createdByUser?.profileImage || createdByUser?.photoURL;
                          return (
                            <>
                              <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs mr-2 overflow-hidden flex-shrink-0 relative">
                                {profileImage ? (
                                  <img 
                                    src={profileImage} 
                                    alt={createdByUser?.name || 'User'} 
                                    className="w-full h-full object-cover absolute inset-0 z-10"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : null}
                                <span className="w-full h-full flex items-center justify-center">
                                  {task.createdBy === 'Internal' ? 'IN' : createdByUser?.initials || 'JD'}
                                </span>
                              </div>
                              <span>{task.createdBy === 'Internal' ? 'Internal' : (createdByUser?.name || 'Unknown User')}</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className={`${isMobile ? '' : isTablet ? 'col-span-1' : 'col-span-2'} flex items-center justify-center text-sm text-gray-600 dark:text-gray-400`}>
                        {task.createdAt ? formatDueDate(task.createdAt) : '—'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination Controls */}
            {getTotalPages() > 1 && (
              <div className="flex items-center justify-between px-6 py-4 mt-4 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {((currentPage - 1) * tasksPerPage) + 1} to {Math.min(currentPage * tasksPerPage, getFilteredTasks().length)} of {getFilteredTasks().length} tasks
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-3.5 py-1.5 rounded-lg border text-sm transition-colors ${
                      currentPage === 1
                        ? 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 bg-white dark:bg-gray-700'
                    }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map(page => {
                      // Show first page, last page, current page, and pages around current
                      const totalPages = getTotalPages();
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3.5 py-1.5 rounded-lg border text-sm transition-colors ${
                              currentPage === page
                                ? 'bg-red-500 text-white border-red-500 shadow-sm'
                                : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 bg-white dark:bg-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="px-2 text-gray-500 dark:text-gray-400">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(getTotalPages(), prev + 1))}
                    disabled={currentPage === getTotalPages()}
                    className={`px-3.5 py-1.5 rounded-lg border text-sm transition-colors ${
                      currentPage === getTotalPages()
                        ? 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-600 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 bg-white dark:bg-gray-700'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Task Modal */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-full max-w-2xl mx-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white">New Task</h2>
              <button 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 absolute right-4"
                onClick={() => setShowNewTaskModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              {/* Task Title - Full width */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input 
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  placeholder="Task title"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              {/* Description - Full width */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea 
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-3 h-32 focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Add details about this task..."
                />
              </div>

              {/* Due Date, Assignees, and Company Row */}
              <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'} ${isMobile ? 'gap-2' : 'gap-4'} mb-4`}>
                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
                  <div className="relative">
                    <CalendarIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
                    <DatePicker
                      selected={newTask.dueDate}
                      onChange={(date) => setNewTask({...newTask, dueDate: date})}
                      placeholderText="Select due date"
                      className="border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-3 py-2 w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Assignees */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign to</label>
                  <button
                    onClick={() => {
                      setShowAssigneesModal(true);
                      setShowNewTaskInternalTab(true);
                      setShowNewTaskAccountsTab(false);
                      setNewTaskAccountSearchTerm('');
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>
                      {newTask.assignees.length > 0 
                        ? newTask.assignees.map(a => a.name).join(', ')
                        : 'Assign to'
                      }
                    </span>
                  </div>
                  <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
                </button>

                {/* Selected Assignees Avatars */}
                {newTask.assignees.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newTask.assignees.map(user => (
                      <div key={user.id} className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 overflow-hidden relative">
                        {user.profileImage || user.photoURL ? (
                          <img 
                            src={user.profileImage || user.photoURL} 
                            alt={user.name || 'User'} 
                            className="w-full h-full object-cover absolute inset-0 z-10"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <span className="w-full h-full flex items-center justify-center">
                          {user.initials}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyModalTarget('new');
                      setShowCompanyInternalTab(true);
                      setShowCompanyAccountsTab(false);
                      setCompanySearchTerm('');
                      setShowCompanyModal(true);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {(() => {
                      const company = getTaskCompanyDisplay(newTask, accounts);
                      if (!company) return <span>Select company</span>;
                      return (
                        <span className="flex items-center gap-2 min-w-0">
                          <CompanyLogoBadge logoUrl={company.logoUrl} name={company.name} />
                          <span className="truncate">{company.name}</span>
                        </span>
                      );
                    })()}
                    <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {newTask.files && newTask.files.length > 0 && (
                <div className="mb-4 space-y-1">
                  {newTask.files.map((file, index) => (
                    <div key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-center justify-between">
                      <span>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setNewTask(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button 
                  className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                  onClick={() => setShowNewTaskModal(false)}
                >
                  Cancel
                </button>
                <label className="flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                  <Upload size={16} />
                  <span>Attach files</span>
                  <input 
                    type="file" 
                    multiple 
                    className="hidden" 
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files);
                      setNewTask({
                        ...newTask,
                        files: [...(newTask.files || []), ...newFiles]
                      });
                    }}
                  />
                </label>
              </div>
              <button 
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                onClick={handleCreateTask}
              >
                Create task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignees Selection Modal */}
      {showAssigneesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-full max-w-2xl mx-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white">Select Assignees</h2>
              <button 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 absolute right-4"
                onClick={() => setShowAssigneesModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  onClick={() => {
                    setShowNewTaskInternalTab(true);
                    setShowNewTaskAccountsTab(false);
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    showNewTaskInternalTab 
                      ? 'border-b-2 border-red-500 text-red-500' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  Internal
                </button>
                <button
                  onClick={() => {
                    setShowNewTaskInternalTab(false);
                    setShowNewTaskAccountsTab(true);
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    showNewTaskAccountsTab 
                      ? 'border-b-2 border-red-500 text-red-500' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  Clients
                </button>
              </div>
              
              {/* Internal Users Tab */}
              {showNewTaskInternalTab && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {users.internal.map(user => (
                    <div
                      key={user.id}
                      onClick={() => toggleAssignee(user)}
                      className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-md border border-gray-200 dark:border-gray-600"
                    >
                      <input
                        type="checkbox"
                        checked={newTask.assignees.some(a => a.id === user.id)}
                        onChange={() => {}}
                        className="mr-3 w-4 h-4"
                      />
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3 overflow-hidden flex-shrink-0 relative">
                        {user.profileImage ? (
                          <img 
                            src={user.profileImage} 
                            alt={user.name || 'User'} 
                            className="w-full h-full object-cover absolute inset-0 z-10"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <span className="w-full h-full flex items-center justify-center">
                          {user.initials}
                        </span>
                      </div>
                      <span className="text-gray-900 dark:text-white font-medium">{user.name}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Clients Tab */}
              {showNewTaskAccountsTab && (
                <div>
                  <input
                    type="text"
                    placeholder="Search clients or team members..."
                    value={newTaskAccountSearchTerm}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-4"
                    onChange={(e) => {
                      setNewTaskAccountSearchTerm(e.target.value);
                    }}
                  />
                  {clientTeamMembersLoading && (
                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">Loading team members...</p>
                  )}
                  <div className="space-y-2 max-h-[22rem] overflow-y-auto">
                    {clientAccounts
                      .filter(account => {
                        const term = newTaskAccountSearchTerm.trim().toLowerCase();
                        if (!term) return true;
                        if ((account.company || '').toLowerCase().includes(term)) return true;
                        // Clients are also findable by any of their team members' names
                        return getClientRoster(account).some(m =>
                          (m.name || '').toLowerCase().includes(term) ||
                          (m.email || '').toLowerCase().includes(term)
                        );
                      })
                      .map((account) => (
                        <div
                          key={account.id}
                          onClick={() => toggleAssignee({
                            id: account.id,
                            name: account.company,
                            type: 'client',
                            initials: account.company?.[0] || 'C',
                            photoURL: getCrmContactDisplayPhotoUrl(account) || null
                          })}
                          className="flex items-start p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-md border border-gray-200 dark:border-gray-600"
                        >
                          <input
                            type="checkbox"
                            checked={newTask.assignees.some(a => a.id === account.id)}
                            onChange={() => {}}
                            className="mr-3 mt-1 w-4 h-4 flex-shrink-0"
                          />
                          <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3 overflow-hidden flex-shrink-0 relative ring-1 ring-gray-200 dark:ring-gray-600">
                            {getCrmCompanyLogoDisplayUrl(account) ? (
                              <img
                                src={getCrmCompanyLogoDisplayUrl(account)}
                                alt={account.company || 'Client'}
                                className="w-full h-full object-contain p-0.5 absolute inset-0 z-10 bg-white dark:bg-gray-700"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : null}
                            <span className="w-full h-full flex items-center justify-center">
                              {(account.company?.[0] || 'C').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="block text-gray-900 dark:text-white font-medium truncate">{account.company}</span>
                            <ClientTeamChips account={account} members={getClientRoster(account)} />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-center gap-4">
              <button 
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                onClick={() => setShowAssigneesModal(false)}
              >
                Cancel
              </button>
              <button 
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                onClick={() => setShowAssigneesModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select Company Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-full max-w-2xl mx-4">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white">Select Company</h2>
              <button 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 absolute right-4"
                onClick={() => setShowCompanyModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <div className="flex border-b border-gray-200 dark:border-gray-600 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanyInternalTab(true);
                    setShowCompanyAccountsTab(false);
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    showCompanyInternalTab 
                      ? 'border-b-2 border-red-500 text-red-500' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  Internal
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanyInternalTab(false);
                    setShowCompanyAccountsTab(true);
                  }}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    showCompanyAccountsTab 
                      ? 'border-b-2 border-red-500 text-red-500' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  Clients
                </button>
              </div>
              {showCompanyInternalTab && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  <div
                    onClick={() => selectCompany({ id: 'internal', name: 'Internal' }, companyModalTarget)}
                    className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-md border border-gray-200 dark:border-gray-600"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white dark:bg-gray-700 flex items-center justify-center mr-3 overflow-hidden flex-shrink-0 ring-1 ring-gray-200 dark:ring-gray-600">
                      <img
                        src="/bba-emblem.svg"
                        alt="Builders Breakthrough Advisors"
                        className="w-full h-full object-contain p-0.5"
                      />
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">Internal</span>
                  </div>
                </div>
              )}
              {showCompanyAccountsTab && (
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Search clients or team members..."
                    value={companySearchTerm}
                    onChange={(e) => setCompanySearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
                  />
                  {clientTeamMembersLoading && (
                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">Loading team members...</p>
                  )}
                  <div className="max-h-[22rem] overflow-y-auto space-y-2">
                    {clientAccounts
                      .filter((account) => {
                        const term = companySearchTerm.trim().toLowerCase();
                        if (!term) return true;
                        if ((account.company || '').toLowerCase().includes(term)) return true;
                        return getClientRoster(account).some(m =>
                          (m.name || '').toLowerCase().includes(term) ||
                          (m.email || '').toLowerCase().includes(term)
                        );
                      })
                      .map((account) => (
                        <div
                          key={account.id}
                          onClick={() => selectCompany({ id: account.id, name: account.company }, companyModalTarget)}
                          className="flex items-start p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer rounded-md border border-gray-200 dark:border-gray-600"
                        >
                          <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 mr-3 overflow-hidden flex-shrink-0 relative ring-1 ring-gray-200 dark:ring-gray-600">
                            {getCrmCompanyLogoDisplayUrl(account) ? (
                              <img
                                src={getCrmCompanyLogoDisplayUrl(account)}
                                alt={account.company || 'Client'}
                                className="w-full h-full object-contain p-0.5 absolute inset-0 z-10 bg-white dark:bg-gray-700"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : null}
                            <span className="w-full h-full flex items-center justify-center">
                              {(account.company?.[0] || 'C').toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="block text-gray-900 dark:text-white font-medium truncate">{account.company}</span>
                            <ClientTeamChips account={account} members={getClientRoster(account)} />
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-center">
              <button 
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                onClick={() => setShowCompanyModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-full max-w-2xl mx-4">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white">Edit Task</h2>
              <button 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 absolute right-4"
                onClick={() => setShowEditTaskModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              {/* Task Title - Full width */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input 
                  type="text"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  placeholder="Task title"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              {/* Description - Full width */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea 
                  value={editingTask.description}
                  onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-3 h-32 focus:ring-1 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Add details about this task..."
                />
              </div>

              {/* Due Date, Assignees, and Company Row */}
              <div className={`grid ${isMobile ? 'grid-cols-1' : isTablet ? 'grid-cols-2' : 'grid-cols-3'} ${isMobile ? 'gap-2' : 'gap-4'} mb-4`}>
                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
                  <div className="relative">
                    <CalendarIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
                    <DatePicker
                      selected={editingTask.dueDate}
                      onChange={(date) => setEditingTask({...editingTask, dueDate: date})}
                      placeholderText="Select due date"
                      className="border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-3 py-2 w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Assignees */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign to</label>
                  <button
                    onClick={() => setEditingTask(prev => ({ ...prev, showAssigneeDropdown: !prev.showAssigneeDropdown }))}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>
                        {editingTask.assignees.length > 0 
                          ? editingTask.assignees.map(a => a.name).join(', ')
                          : 'Assign to'
                        }
                      </span>
                    </div>
                    <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
                  </button>

                  {/* Selected Assignees Avatars */}
                  {editingTask.assignees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {editingTask.assignees.map(user => (
                        <div key={user.id} className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300 overflow-hidden relative">
                          {user.profileImage || user.photoURL ? (
                            <img 
                              src={user.profileImage || user.photoURL} 
                              alt={user.name || 'User'} 
                              className="w-full h-full object-cover absolute inset-0 z-10"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : null}
                          <span className="w-full h-full flex items-center justify-center">
                            {user.initials}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {editingTask.showAssigneeDropdown && (
                    <div 
                      className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg assignee-dropdown"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between p-2 border-b border-gray-200 dark:border-gray-600">
                        <h3 className="font-medium text-gray-700 dark:text-gray-300">Select Assignees</h3>
                        <button 
                          onClick={() => setEditingTask(prev => ({ ...prev, showAssigneeDropdown: false }))}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="p-2">
                        {users.internal.map(user => (
                          <div
                            key={user.id}
                            onClick={() => toggleEditAssignee(user)}
                            className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={editingTask.assignees.some(a => a.id === user.id)}
                              onChange={() => {}}
                              className="mr-2"
                            />
                            <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 mr-2 overflow-hidden flex-shrink-0 relative">
                              {user.profileImage ? (
                                <img 
                                  src={user.profileImage} 
                                  alt={user.name || 'User'} 
                                  className="w-full h-full object-cover absolute inset-0 z-10"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : null}
                              <span className="w-full h-full flex items-center justify-center">
                                {user.initials}
                              </span>
                            </div>
                            <span className="text-gray-900 dark:text-white">{user.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyModalTarget('edit');
                      setShowCompanyInternalTab(true);
                      setShowCompanyAccountsTab(false);
                      setCompanySearchTerm('');
                      setShowCompanyModal(true);
                    }}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {(() => {
                      const company = getTaskCompanyDisplay(editingTask, accounts);
                      if (!company) return <span>Select company</span>;
                      return (
                        <span className="flex items-center gap-2 min-w-0">
                          <CompanyLogoBadge logoUrl={company.logoUrl} name={company.name} />
                          <span className="truncate">{company.name}</span>
                        </span>
                      );
                    })()}
                    <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {editingTask.files && editingTask.files.length > 0 && (
                <div className="mb-4 space-y-1">
                  {editingTask.files.map((file, index) => (
                    <div key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {file.fileName || file.name}
                    </div>
                  ))}
                </div>
              )}

            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-between items-center">
              <div>
                {editingTask.createdBy === auth.currentUser.uid && (
                  <button 
                    className="bg-red-800 text-white px-4 py-2 rounded-md hover:bg-red-900"
                    onClick={handleDeleteTask}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button 
                  className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                  onClick={() => setShowEditTaskModal(false)}
                >
                  Cancel
                </button>
                <label className="flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800">
                  <Upload size={16} />
                  <span>Attach files</span>
                  <input 
                    type="file" 
                    multiple 
                    className="hidden" 
                    onChange={async (e) => {
                      const newFiles = Array.from(e.target.files);
                      if (newFiles.length > 0) {
                        try {
                          const uploadPromises = newFiles.map(file => uploadTaskFile(file, editingTask.id));
                          const uploadedFiles = await Promise.all(uploadPromises);
                          setEditingTask({
                            ...editingTask,
                            files: [...(editingTask.files || []), ...uploadedFiles]
                          });
                        } catch (error) {
                          console.error('Error uploading files:', error);
                          setError(`Failed to upload files: ${error.message}`);
                        }
                      }
                    }}
                  />
                </label>
                <button 
                  className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                  onClick={handleSaveEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Task Modal */}
      {showViewTaskModal && viewingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-full max-w-2xl mx-4">
            {/* Modal Header */}
            <div className="relative flex justify-center items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium w-full text-center text-gray-900 dark:text-white">Task Details</h2>
              <button 
                className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 z-10 bg-white dark:bg-gray-800 rounded-full p-1 shadow"
                onClick={() => setShowViewTaskModal(false)}
                aria-label="Close task details"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              {/* Task Title */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <div className="text-lg text-gray-900 dark:text-white">{viewingTask.title}</div>
              </div>
              
              {/* Description */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <div className="text-gray-600 dark:text-gray-400">{viewingTask.description || 'No description provided'}</div>
              </div>

              {/* Due Date */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
                <div className="text-gray-600 dark:text-gray-400">
                  {formatDueDate(viewingTask.dueDate)}
                </div>
              </div>

              {/* Company */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <TaskCompanyLink company={getTaskCompanyDisplay(viewingTask, accounts)} />
                </div>
              </div>

              {/* Created by */}
              {viewingTask.createdBy && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Created by</label>
                  <div className="flex items-center gap-2">
                    {isFathomTask(viewingTask) ? (
                      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                        <img src="/fathomlogo.png" alt="Fathom" className="w-8 h-8 rounded-full object-contain" />
                        <span className="text-sm text-gray-900 dark:text-white">Fathom</span>
                      </div>
                    ) : (() => {
                      const createdByUser = viewingTask.createdBy === 'Internal' ? null : (users.internal.find(u => u.id === viewingTask.createdBy) || clientUsers[viewingTask.createdBy]);
                      const profileImage = createdByUser?.profileImage || createdByUser?.photoURL;
                      return (
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm overflow-hidden flex-shrink-0 relative">
                            {profileImage ? (
                              <img 
                                src={profileImage} 
                                alt={createdByUser?.name || 'User'} 
                                className="w-full h-full object-cover absolute inset-0 z-10"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : null}
                            <span className="w-full h-full flex items-center justify-center">
                              {viewingTask.createdBy === 'Internal' ? 'IN' : createdByUser?.initials || 'JD'}
                            </span>
                          </div>
                          <span className="text-sm text-gray-900 dark:text-white">{viewingTask.createdBy === 'Internal' ? 'Internal' : (createdByUser?.name || 'Unknown User')}</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Assignees */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assignees</label>
                <div className="flex flex-wrap gap-2">
                  {viewingTask.assignees.map(user => (
                    <div key={user.id} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm overflow-hidden flex-shrink-0 relative">
                        {(user.profileImage || user.photoURL) ? (
                          <img 
                            src={user.profileImage || user.photoURL} 
                            alt={user.name || 'User'} 
                            className="w-full h-full object-cover absolute inset-0 z-10"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : null}
                        <span className="w-full h-full flex items-center justify-center">
                          {user.initials}
                        </span>
                      </div>
                      <span className="text-sm text-gray-900 dark:text-white">{user.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Attachments */}
              {viewingTask.files && viewingTask.files.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attachments</label>
                  <div className="space-y-2">
                    {viewingTask.files.map((file, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-md">
                        <Upload size={16} className="text-gray-500 dark:text-gray-400" />
                        {file.fileUrl ? (
                          <a 
                            href={file.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {file.fileName || file.name}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-white">{file.fileName || file.name}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t flex justify-between items-center">
              <div>
                {viewingTask.createdBy === auth.currentUser?.uid && (
                  <button 
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSwitchToEdit();
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="flex gap-4">
                {!viewingTask.completed && (
                  <button 
                    className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        await handleTaskCompletion(viewingTask.id, e);
                        setViewingTask(prev => ({ ...prev, completed: true }));
                      } catch (err) {
                        console.error('Error marking task as completed:', err);
                      }
                    }}
                  >
                    Mark as completed
                  </button>
                )}
                <button 
                  className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white bg-white dark:bg-gray-800"
                  onClick={() => setShowViewTaskModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Tasks; 
